import { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  message,
  Typography,
  Row,
  Col,
  Statistic,
  Progress,
  Descriptions,
  List,
  Avatar,
  Popconfirm,
  Switch,
  Tooltip,
} from 'antd';
import {
  TrophyOutlined,
  PlusOutlined,
  GiftOutlined,
  CarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DollarOutlined,
  UserOutlined,
  FireOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCustom, useApiUrl } from '@refinedev/core';
import { apiClient } from '../../lib/api-client';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const tripTypeLabels: Record<string, string> = {
  INTERCITY_TAXI: 'Shahar ichida',
  INTERREGION_TAXI: 'Viloyatlararo',
  SHAHARLAR_ARO: 'Shaharlararo',
  YOL_YOLAKAY: "Yo'l-Yo'lakay",
  POSTAGE: 'Pochta',
  CARGO: 'Yuk',
  TAXI: 'Taxi',
  DELIVERY: 'Yetkazish',
};

const tripTypeColors: Record<string, string> = {
  INTERCITY_TAXI: 'blue',
  INTERREGION_TAXI: 'purple',
  SHAHARLAR_ARO: 'geekblue',
  YOL_YOLAKAY: 'volcano',
  POSTAGE: 'orange',
  CARGO: 'cyan',
  TAXI: 'blue',
  DELIVERY: 'orange',
};

interface Incentive {
  id: number;
  name: string;
  description: string;
  tripCountTarget: number;
  bonusAmount: number;
  startDate: string;
  endDate: string;
  tripTypes: string[];
  isActive: boolean;
  createdAt: string;
  participantCount: number;
  completedCount: number;
  totalBonusPaid: number;
}

interface DriverProgress {
  id: number;
  driverId: number;
  driverName: string;
  driverPhone: string;
  driverPhoto?: string;
  currentTripCount: number;
  targetTripCount: number;
  progress: number;
  isCompleted: boolean;
  bonusPaid: boolean;
  completedAt?: string;
}

interface IncentiveStats {
  totalIncentives: number;
  activeIncentives: number;
  totalParticipants: number;
  totalBonusPaid: number;
}

export const IncentiveList = () => {
  const apiUrl = useApiUrl();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedIncentive, setSelectedIncentive] = useState<Incentive | null>(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // Fetch incentives
  const { data: incentivesData, isLoading, refetch } = useCustom<{
    data: Incentive[];
    total: number;
  }>({
    url: `${apiUrl}/admin/incentives`,
    method: 'get',
  });

  // Fetch stats
  const { data: statsData } = useCustom<IncentiveStats>({
    url: `${apiUrl}/admin/incentives/stats`,
    method: 'get',
  });

  // Fetch driver progress for selected incentive
  const { data: progressData, isLoading: progressLoading, refetch: refetchProgress } = useCustom<{
    data: DriverProgress[];
  }>({
    url: `${apiUrl}/admin/incentives/${selectedIncentive?.id}/progress`,
    method: 'get',
    queryOptions: {
      enabled: !!selectedIncentive,
    },
  });

  const stats = statsData?.data;
  const incentives = incentivesData?.data?.data || [];
  const driverProgress = progressData?.data?.data || [];

  const handleCreateIncentive = async (values: any) => {
    setSaving(true);
    try {
      const payload = {
        ...values,
        startDate: values.dateRange[0].toISOString(),
        endDate: values.dateRange[1].toISOString(),
      };
      delete payload.dateRange;

      if (selectedIncentive) {
        await apiClient.put(`/admin/incentives/${selectedIncentive.id}`, payload);
        message.success('Dastur yangilandi');
      } else {
        await apiClient.post('/admin/incentives', payload);
        message.success('Dastur yaratildi');
      }
      setCreateModalOpen(false);
      form.resetFields();
      setSelectedIncentive(null);
      refetch();
    } catch (error) {
      message.error('Xatolik yuz berdi');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteIncentive = async (id: number) => {
    try {
      await apiClient.delete(`/admin/incentives/${id}`);
      message.success("Dastur o'chirildi");
      refetch();
    } catch (error) {
      message.error("Dasturni o'chirishda xatolik");
    }
  };

  const handleToggleActive = async (id: number, isActive: boolean) => {
    try {
      await apiClient.patch(`/admin/incentives/${id}/toggle`, { isActive });
      message.success(isActive ? 'Dastur faollashtirildi' : "Dastur to'xtatildi");
      refetch();
    } catch (error) {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleAwardBonus = async (progressId: number) => {
    try {
      await apiClient.post(`/admin/incentives/award/${progressId}`);
      message.success('Bonus berildi');
      refetchProgress();
      refetch();
    } catch (error) {
      message.error('Bonus berishda xatolik');
    }
  };

  const handleViewDetails = (incentive: Incentive) => {
    setSelectedIncentive(incentive);
    setDetailsModalOpen(true);
  };

  const handleEditIncentive = (incentive: Incentive) => {
    setSelectedIncentive(incentive);
    form.setFieldsValue({
      name: incentive.name,
      description: incentive.description,
      tripCountTarget: incentive.tripCountTarget,
      bonusAmount: incentive.bonusAmount,
      tripTypes: incentive.tripTypes,
      dateRange: [dayjs(incentive.startDate), dayjs(incentive.endDate)],
    });
    setCreateModalOpen(true);
  };

  const columns: ColumnsType<Incentive> = [
    {
      title: 'Dastur nomi',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      render: (name, record) => (
        <div>
          <Text strong>{name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.description}
          </Text>
        </div>
      ),
    },
    {
      title: 'Maqsad',
      key: 'target',
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>
            <CarOutlined /> {record.tripCountTarget} ta sayohat
          </Text>
          <Text type="success">
            <GiftOutlined /> {record.bonusAmount.toLocaleString()} so'm bonus
          </Text>
        </Space>
      ),
    },
    {
      title: 'Muddat',
      key: 'period',
      width: 130,
      render: (_, record) => (
        <Text>
          {dayjs(record.startDate).format('DD.MM.YYYY')} – {dayjs(record.endDate).format('DD.MM.YYYY')}
        </Text>
      ),
    },
    {
      title: 'Sayohat turlari',
      dataIndex: 'tripTypes',
      key: 'tripTypes',
      width: 240,
      render: (types: string[]) => (
        <Space wrap size={[4, 4]}>
          {types.map((type) => (
            <Tag key={type} color={tripTypeColors[type] || 'default'}>
              {tripTypeLabels[type] || type}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Ishtirokchilar',
      key: 'participants',
      width: 140,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.participantCount} ta ishtirokchi</Text>
          <Text type="success">{record.completedCount} ta bajargan</Text>
        </Space>
      ),
    },
    {
      title: 'Holat',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive, record) => {
        const now = dayjs();
        const endDate = dayjs(record.endDate);
        const isExpired = endDate.isBefore(now);

        if (isExpired) {
          return <Tag color="default">Tugagan</Tag>;
        }
        return (
          <Switch
            checked={isActive}
            onChange={(checked) => handleToggleActive(record.id, checked)}
            checkedChildren="Faol"
            unCheckedChildren="Nofaol"
          />
        );
      },
    },
    {
      title: 'Amallar',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Batafsil">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title="Tahrirlash">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditIncentive(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Dasturni o'chirishni xohlaysizmi?"
            onConfirm={() => handleDeleteIncentive(record.id)}
            okText="Ha"
            cancelText="Yo'q"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Jami dasturlar"
              value={stats?.totalIncentives || 0}
              prefix={<TrophyOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Faol dasturlar"
              value={stats?.activeIncentives || 0}
              prefix={<FireOutlined />}
              valueStyle={{ color: '#27AE60' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Jami ishtirokchilar"
              value={stats?.totalParticipants || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#3498DB' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Berilgan bonuslar"
              value={stats?.totalBonusPaid || 0}
              prefix={<DollarOutlined />}
              suffix="so'm"
              valueStyle={{ color: '#9B59B6' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Card
        title={
          <Space>
            <TrophyOutlined />
            <span>Haydovchi rag'batlantirish dasturlari</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setSelectedIncentive(null);
              form.resetFields();
              setCreateModalOpen(true);
            }}
          >
            Yangi dastur
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={incentives}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={selectedIncentive ? 'Dasturni tahrirlash' : 'Yangi dastur yaratish'}
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          form.resetFields();
          setSelectedIncentive(null);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateIncentive}
        >
          <Form.Item
            name="name"
            label="Dastur nomi"
            rules={[{ required: true, message: 'Nom kiriting' }]}
          >
            <Input placeholder="Masalan: Haftalik bonus" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Tavsif"
            rules={[{ required: true, message: 'Tavsif kiriting' }]}
          >
            <Input.TextArea placeholder="Dastur haqida qisqacha" rows={2} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="tripCountTarget"
                label="Sayohat soni maqsadi"
                rules={[{ required: true, message: 'Maqsad kiriting' }]}
              >
                <InputNumber
                  min={1}
                  style={{ width: '100%' }}
                  placeholder="Masalan: 50"
                  addonAfter="ta"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="bonusAmount"
                label="Bonus miqdori"
                rules={[{ required: true, message: 'Bonus kiriting' }]}
              >
                <InputNumber
                  min={1000}
                  step={1000}
                  style={{ width: '100%' }}
                  placeholder="Masalan: 100000"
                  addonAfter="so'm"
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value!.replace(/,/g, '') as any}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="tripTypes"
            label="Sayohat turlari"
            rules={[{ required: true, message: 'Kamida bitta tur tanlang' }]}
          >
            <Select
              mode="multiple"
              placeholder="Sayohat turlarini tanlang"
              options={[
                { value: 'INTERCITY_TAXI', label: 'Shahar ichida' },
                { value: 'INTERREGION_TAXI', label: 'Viloyatlararo' },
                { value: 'POSTAGE', label: 'Pochta' },
                { value: 'CARGO', label: 'Yuk' },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="dateRange"
            label="Muddat"
            rules={[{ required: true, message: 'Muddatni tanlang' }]}
          >
            <RangePicker
              style={{ width: '100%' }}
              format="DD.MM.YYYY"
              placeholder={['Boshlanish', 'Tugash']}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setCreateModalOpen(false)}>Bekor qilish</Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                Saqlash
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Details Modal */}
      <Modal
        title={
          <Space>
            <TrophyOutlined />
            <span>{selectedIncentive?.name}</span>
          </Space>
        }
        open={detailsModalOpen}
        onCancel={() => {
          setDetailsModalOpen(false);
          setSelectedIncentive(null);
        }}
        footer={null}
        width={800}
      >
        {selectedIncentive && (
          <div>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 24 }}>
              <Descriptions.Item label="Tavsif" span={2}>
                {selectedIncentive.description}
              </Descriptions.Item>
              <Descriptions.Item label="Maqsad">
                {selectedIncentive.tripCountTarget} ta sayohat
              </Descriptions.Item>
              <Descriptions.Item label="Bonus">
                {selectedIncentive.bonusAmount.toLocaleString()} so'm
              </Descriptions.Item>
              <Descriptions.Item label="Boshlanish">
                {dayjs(selectedIncentive.startDate).format('DD.MM.YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Tugash">
                {dayjs(selectedIncentive.endDate).format('DD.MM.YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Ishtirokchilar">
                {selectedIncentive.participantCount} ta
              </Descriptions.Item>
              <Descriptions.Item label="Bajarganlar">
                {selectedIncentive.completedCount} ta
              </Descriptions.Item>
            </Descriptions>

            <Title level={5}>
              <UserOutlined /> Haydovchilar jarayoni
            </Title>

            <List
              loading={progressLoading}
              dataSource={driverProgress}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    item.isCompleted && !item.bonusPaid && (
                      <Button
                        type="primary"
                        size="small"
                        icon={<GiftOutlined />}
                        onClick={() => handleAwardBonus(item.id)}
                      >
                        Bonus berish
                      </Button>
                    ),
                    item.bonusPaid && (
                      <Tag color="success" icon={<CheckCircleOutlined />}>
                        Bonus berilgan
                      </Tag>
                    ),
                  ].filter(Boolean)}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        src={item.driverPhoto}
                        icon={!item.driverPhoto && <UserOutlined />}
                      />
                    }
                    title={
                      <Space>
                        <span>{item.driverName}</span>
                        {item.isCompleted && (
                          <Tag color="success" icon={<CheckCircleOutlined />}>
                            Bajarildi
                          </Tag>
                        )}
                      </Space>
                    }
                    description={item.driverPhone}
                  />
                  <div style={{ width: 200 }}>
                    <Progress
                      percent={item.progress}
                      size="small"
                      status={item.isCompleted ? 'success' : 'active'}
                      format={() => `${item.currentTripCount}/${item.targetTripCount}`}
                    />
                  </div>
                </List.Item>
              )}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};
